import { useEffect, useRef } from 'react';

import { selectorFamily, useRecoilCallback, useRecoilValue } from 'recoil';

import { SHELL_PORTAL } from 'lib/context/ShellContext/shellPortal';
import { PORTAL_ACCOUNT, accountIdOf } from 'lib/helpers/sessionPersistedAtom';

import { dockState } from 'components/Dock/lib/dockState';
import { dockTabsState } from 'components/GlobalDock/lib/dockTabsState';
import { openChartsState } from 'components/practice/charts/lib/openChartsState';
import { viewedPracticesState } from 'components/GlobalDock/lib/viewedPracticesState';
import { forgetSavedWorkspaces, loadedWorkspacesOf } from 'components/GlobalDock/lib/dockWorkspace';
import { exportProgressState } from 'components/practice/Export/ExportDockCard/lib/exportProgressState';
import { batchEligibilityProgressState } from 'components/practice/Eligibility/BatchEligibility/components/EligibilityDockCard/lib/batchEligibilityProgressState';

const WORKSPACE_FAMILIES = [dockTabsState, openChartsState];

const PORTAL_ATOMS = {
  [SHELL_PORTAL.PRACTICE]: [dockState, batchEligibilityProgressState, exportProgressState],
  [SHELL_PORTAL.ADMIN]: [viewedPracticesState]
};

// A selector, so the app root re-renders only when the signed-in account changes, not on every
// write to the user or admin record.
const accountIdState = selectorFamily({
  key: 'session/accountId',
  get:
    (portal) =>
    ({ get }) =>
      accountIdOf(get(PORTAL_ACCOUNT[portal]))
});

const useResetOnAccountChange = (portal) => {
  const accountId = useRecoilValue(accountIdState(portal));
  const lastAccountId = useRef(accountId);
  // Every practice's workspace goes, not only the one on screen.
  const resetAll = useRecoilCallback(
    ({ reset }) =>
      () => {
        loadedWorkspacesOf(portal).forEach((workspace) =>
          WORKSPACE_FAMILIES.forEach((family) => reset(family(workspace)))
        );
        PORTAL_ATOMS[portal].forEach((atom) => reset(atom));
        forgetSavedWorkspaces(portal);
      },
    [portal]
  );

  useEffect(() => {
    const previous = lastAccountId.current;
    lastAccountId.current = accountId;
    if (previous && previous !== accountId) resetAll();
  }, [accountId, resetAll]);
};

// Logging out never reloads the page, so without this the atoms would keep the last account's
// patients and tabs for whoever signs in next in this browser tab.
export const useResetSessionOnAccountChange = () => {
  useResetOnAccountChange(SHELL_PORTAL.PRACTICE);
  useResetOnAccountChange(SHELL_PORTAL.ADMIN);
};
